import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronDown, Menu, Phone, X } from "lucide-react";
import { SERVICES, SITE } from "@/data/site";

const NAV_LINKS = [
  { to: "/meet-jeremy", label: "Meet Jeremy", testid: "nav-meet-jeremy-link" },
  { to: "/about", label: "About Advantage", testid: "nav-about-link" },
  { to: "/insurance-new-patients", label: "Insurance & New Patients", testid: "nav-insurance-link" },
  { to: "/faqs", label: "FAQs", testid: "nav-faqs-link" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className="sticky top-0 z-50 border-b border-slate-100 bg-white/90 backdrop-blur-md"
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-12">
          <Link to="/" data-testid="nav-home-logo" className="flex shrink-0 items-center">
            <img
              src="/images/logo-mark.png"
              alt="Advantage Primary Health"
              className="h-11 w-auto"
            />
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Main navigation">
            <div className="group relative">
              <button
                type="button"
                data-testid="nav-services-dropdown"
                className="flex items-center gap-1 text-sm font-medium text-slate-700 transition-colors duration-300 hover:text-brand-red"
              >
                Services
                <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
              </button>
              <div className="invisible absolute left-1/2 top-full w-72 -translate-x-1/2 pt-4 opacity-0 transition-[opacity,transform] duration-300 group-hover:visible group-hover:opacity-100">
                <div className="rounded-2xl border border-slate-100 bg-white p-2 shadow-lift">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`nav-service-${s.slug}`}
                      className="flex items-center justify-between rounded-xl px-4 py-3 text-sm font-medium text-slate-700 transition-colors duration-200 hover:bg-brand-redlight hover:text-brand-red"
                    >
                      {s.name}
                      <ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition-opacity duration-200 group-hover:opacity-40" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={link.testid}
                className="text-sm font-medium text-slate-700 transition-colors duration-300 hover:text-brand-red"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden items-center gap-5 lg:flex">
            <a
              href={SITE.phoneHref}
              data-testid="nav-call-link"
              className="group flex flex-col items-end leading-tight"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-red">
                Same-Day Care
              </span>
              <span className="flex items-center gap-1.5 text-sm font-semibold text-brand-navy transition-colors duration-300 group-hover:text-brand-red">
                <Phone className="h-3.5 w-3.5" />
                {SITE.phone}
              </span>
            </a>
            <a
              href={SITE.bookUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-online-btn"
              className="rounded-full bg-brand-red px-6 py-3 text-sm font-semibold text-white shadow-red transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-brand-redhover"
            >
              Book Online
            </a>
          </div>

          <button
            type="button"
            data-testid="nav-mobile-menu-btn"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen(!open)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 text-brand-navy lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 overflow-y-auto bg-white pt-24 lg:hidden"
          >
            <nav className="flex flex-col px-6 pb-10" aria-label="Mobile navigation">
              <Link
                to="/"
                data-testid="mobile-nav-home-link"
                className="border-b border-slate-100 py-4 font-display text-2xl font-bold text-brand-navy"
              >
                Home
              </Link>
              <p className="pt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-brand-red">
                Services
              </p>
              {SERVICES.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`mobile-nav-service-${s.slug}`}
                  className="border-b border-slate-100 py-4 font-display text-xl font-semibold text-brand-navy"
                >
                  {s.name}
                </Link>
              ))}
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`mobile-${link.testid}`}
                  className="border-b border-slate-100 py-4 font-display text-xl font-semibold text-brand-navy"
                >
                  {link.label}
                </Link>
              ))}
              <div className="mt-8 flex flex-col gap-3">
                <a
                  href={SITE.bookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-book-online-btn"
                  className="rounded-full bg-brand-red px-6 py-4 text-center text-sm font-semibold text-white"
                >
                  Book Online
                </a>
                <a
                  href={SITE.phoneHref}
                  data-testid="mobile-call-btn"
                  className="flex items-center justify-center gap-2 rounded-full border border-slate-300 px-6 py-4 text-sm font-semibold text-brand-navy"
                >
                  <Phone className="h-4 w-4" />
                  Call for Same-Day Care · {SITE.phone}
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
